import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { FormGroup } from '@angular/forms';
import { DateFormControl } from '@form/date-form-control/date-form-control';
import { MemberNumberFormControl } from '@form/member-number-form-control/member-number-form-control';
import { getDobLimits } from '@shared/extensions/extensions';
import { LabelTextPipe } from '@shared/pipes/label-text.pipe';
import { AuthorizationDeliveryMode, ClientCodeRequest, UserLoginInfo } from '@shared/webapi/MIF.Subscription.WebApi';
import { CustomerService } from '@shared/webapi/services/customer.service';
import { WsReferentielMetierService } from '@shared/webapi/services/ws-referentiel-metier.service';
import { ClientFacade } from '../../../../store/client-store/client.facade';
import {
  ClientAuthErrorCode,
  ClientAuthorizationStatus,
  RequestCodeClientAuthError,
  ValidateCodeClientAuthError
} from '../../../../store/client-store/client.models';

@Component({
  standalone: false,
  selector: 'app-authentication-client',
  templateUrl: './authentication-client.component.html',
  styleUrls: ['./authentication-client.component.scss']
})
export class AuthenticationClientComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  authenticationForm: FormGroup = new FormGroup({});
  dobMaxDate: Date | null = null;
  dobAuthValueIsNotInRangeLimit = false;

  requestCodeAuthErrorMessage: string | null = null;
  clientAuthResponse: UserLoginInfo | null = null;
  clientAuthorizationStatus = ClientAuthorizationStatus.Idle;
  isOtpAccessBlocked = false;
  isSubmittingClientAuthRequest = false;
  isAuthorizingClient = false;

  constructor(
    private route: ActivatedRoute,
    private clientFacade: ClientFacade,
    private customerService: CustomerService,
    private wsReferentielMetierService: WsReferentielMetierService,
    private labelTextPipe: LabelTextPipe
  ) {
    this.initForm();
    this.initSubscriptions();
  }

  async ngOnInit(): Promise<void> {
    await this.fetchInitData();
    this.restoreAuthModelFromProfile();
  }

  private async fetchInitData(): Promise<void> {
    this.dobMaxDate = getDobLimits(await this.wsReferentielMetierService.getDobLimits()).dobMaxDate;
  }

  private initForm(): void {
    this.authenticationForm = new FormGroup({
      socialNumberA: new MemberNumberFormControl(true, false),
      dateOfBirth: new DateFormControl(true, false, {
        requiredErrorMessage: this.labelTextPipe.transform('HOME.AlreadyClientDateOfBirthError'),
        minDateErrorMessage: this.labelTextPipe.transform('HOME.AlreadyClientDateOfBirthError'),
        maxDateErrorMessage: this.labelTextPipe.transform('HOME.AlreadyClientDateOfBirthError')
      })
    });
  }

  private initSubscriptions(): void {
    this.clientFacade.authResponse$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(authResponse => {
      this.clientAuthResponse = authResponse;
    });

    this.clientFacade.requestCodeAuthError$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((authError: RequestCodeClientAuthError | null) => {
        this.requestCodeAuthErrorMessage = this.getRequestCodeAuthErrorMessage(authError);
      });

    this.clientFacade.validateCodeAuthError$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((authError: ValidateCodeClientAuthError | null) => {
        this.isOtpAccessBlocked = authError?.errorCode === ClientAuthErrorCode.AccessBlocked;

        if (authError && !this.clientAuthResponse) {
          this.clientAuthResponse = null;
        }
      });

    this.clientFacade.clientAuthorizationStatus$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(clientAuthorizationStatus => {
        this.clientAuthorizationStatus = clientAuthorizationStatus;
      });

    this.clientFacade.isSubmittingClientAuthRequest$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(isSubmittingClientAuthRequest => {
        this.isSubmittingClientAuthRequest = isSubmittingClientAuthRequest;
      });

    this.clientFacade.isAuthorizingClient$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(isAuthorizingClient => {
      this.isAuthorizingClient = isAuthorizingClient;
    });
  }

  private restoreAuthModelFromProfile(): void {
    const userId = this.route.snapshot.queryParamMap.get('userId')?.trim();
    const profile = this.customerService.userData;

    if (!userId || !profile?.uniqueId || profile.uniqueId !== userId) {
      return;
    }

    if (profile.isMember !== true && !profile.socialNumberA) {
      return;
    }

    const authModel = this.customerService.getAuthModel();

    if (!authModel) {
      return;
    }

    this.authenticationForm.patchValue(authModel);
    this.authenticationForm.markAsPristine();
    this.authenticationForm.markAsUntouched();
  }

  onSubmitClientAuth(): void {
    if (!this.isAuthFormValid) {
      this.authenticationForm.markAllAsTouched();
      return;
    }

    const clientId = this.authenticationForm.get('socialNumberA')?.value;
    const dateOfBirth = this.authenticationForm.get('dateOfBirth')?.value;

    if (!clientId || !dateOfBirth) {
      this.authenticationForm.markAllAsTouched();
      return;
    }

    const request = new ClientCodeRequest({
      clientId,
      dateOfBirth: new Date(dateOfBirth),
      deliveryMode: AuthorizationDeliveryMode.Sms
    });

    this.clientFacade.submitClientAuthentication(request);
  }

  private getRequestCodeAuthErrorMessage(authError: RequestCodeClientAuthError | null): string | null {
    if (!authError) {
      return null;
    }

    if (authError.errorCode === ClientAuthErrorCode.InvalidCredentials) {
      return 'Numero de client ou date de naissance invalide.';
    }

    if (authError.errorCode === ClientAuthErrorCode.AccessBlocked) {
      return null;
    }

    return authError.detail;
  }

  onOtpCancel(): void {
    this.clientFacade.cancelClientAuthentication();
  }

  get showOtpCheckScreen(): boolean {
    return (
      !!this.clientAuthResponse &&
      this.clientAuthorizationStatus !== ClientAuthorizationStatus.Authorized &&
      (this.clientAuthorizationStatus !== ClientAuthorizationStatus.Blocked || this.isOtpAccessBlocked) &&
      this.clientAuthorizationStatus !== ClientAuthorizationStatus.Unauthorized
    );
  }

  get isAuthorizedClient(): boolean {
    return this.clientAuthorizationStatus === ClientAuthorizationStatus.Authorized;
  }

  get isBlockedProfile(): boolean {
    return this.clientAuthorizationStatus === ClientAuthorizationStatus.Blocked && !this.isOtpAccessBlocked;
  }

  get isAuthFormValid(): boolean {
    return (
      this.authenticationForm.valid &&
      !this.isSubmittingClientAuthRequest &&
      !this.isAuthorizingClient &&
      !this.isBlockedProfile
    );
  }

  get isBusy(): boolean {
    return this.isSubmittingClientAuthRequest || this.isAuthorizingClient;
  }

  get dobErrorBlockText(): string {
    return this.labelTextPipe.transform('Common.dobErrorBlockText');
  }
}
